/**
 * Sorts an array of row objects based on a specified field and direction
 *
 * @param {Array<Object>} rows - Array of row objects to sort
 * @param {string} sortField - Object key to sort by
 * @param {('asc'|'desc')} direction - Sort direction, either 'asc' for ascending or 'desc' for descending
 * @returns {Array<Object>} Sorted array of row objects
 */
function sortRows(rows, sortField, direction) {
    if (!sortField || !direction || !rows.length) return rows;

    return [...rows].sort((a, b) => {
        if (typeof a[sortField] === 'number' && typeof b[sortField] === 'number') {
            return direction === 'asc' ? a[sortField] - b[sortField] : b[sortField] - a[sortField];
        }

        const valueA = (a[sortField] || '').toString().toLowerCase();
        const valueB = (b[sortField] || '').toString().toLowerCase();
        return direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
}

/**
 * Filters an array of row objects based on a search query
 *
 * @param {Array<Object>} rows - Array of row objects to filter
 * @param {string} searchQuery - Search string to filter rows by
 * @returns {Array<Object>} Filtered array of row objects where any field contains the search query
 */
function filterRows(rows, searchQuery) {
    if (!searchQuery) return rows;

    return rows.filter((row) =>
        Object.values(row).some((value) => value?.toString().toLowerCase().includes(searchQuery.toLowerCase()))
    );
}

/**
 * Custom hook that combines filtering and sorting functionality for table rows
 *
 * @param {Array<Object>} rows - Array of row objects to filter and sort
 * @param {string} searchQuery - Search string to filter rows by
 * @param {Object} sortState - Object containing sorting configuration
 * @param {string} sortState.key - Field key to sort by
 * @param {('asc'|'desc')} sortState.direction - Sort direction
 * @returns {Array<Object>} Filtered and sorted array of row objects
 */
function useFilteredAndSortedRows(rows = [], searchQuery, sortState) {
    const filteredRows = filterRows(rows, searchQuery);

    if (!sortState.direction) {
        return filteredRows;
    }

    return sortRows(filteredRows, sortState.key, sortState.direction);
}

export default useFilteredAndSortedRows;
